import React, { useEffect, useRef, useState } from "react";
import ArabicSentence from "../ArabicSentence";
import WordMorphology from "../WordMorphology";
import useIrabAnalysis from "../../hooks/useIrabAnalysis";
import { track } from "../../utils/analytics";
import { DEMO_VERSES } from "./onboardingContent";
import FALLBACK from "./onboardingDemoFallback.json";

const ALL_COLORS = { verb: true, noun: true, pronoun: true, prepositions: true };

/**
 * OnboardingDemo — the funnel's signature moment: a real, tappable Quran
 * verse analyzed by the same pipeline as the Iʿrāb tool.
 *
 * The verses are from the built-in Quran curriculum, so the morphology cache
 * is prewarmed and each analyze is a cache hit (no AI spend). If the tool is
 * disabled, the per-IP free quota is exhausted, or the network fails, we
 * silently render a committed static analysis instead — a first-time visitor
 * must never see an error here.
 */
export default function OnboardingDemo({ onContinue }) {
  const [verse, setVerse] = useState(DEMO_VERSES[0]);
  const [structureWord, setStructureWord] = useState(null); // { key, morph }
  const [fallback, setFallback] = useState(false);
  const [tapped, setTapped] = useState(false);
  const autoRan = useRef(false);
  const { sentence, tokens, refining, loading, error, upsell, analyze } =
    useIrabAnalysis({ context: "onboarding" });

  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    analyze(DEMO_VERSES[0].text);
    track("onboarding-demo-analyze", { verse: DEMO_VERSES[0].key });
  }, [analyze]);

  // Any failure to produce tappable words → static fallback, silently. That
  // includes a "successful" analyze where every morphology is null (cold
  // cache + engine offline + free tier can't refine) — tappable words are
  // the whole point of this step.
  useEffect(() => {
    if (fallback) return;
    if (upsell === "quota" || error) {
      setFallback(true);
      track("onboarding-demo-fallback", { reason: upsell === "quota" ? "quota" : "error" });
      return;
    }
    if (!loading && tokens && refining.size === 0 &&
        Object.values(tokens).every((t) => !t.morphology)) {
      setFallback(true);
      track("onboarding-demo-fallback", { reason: "empty" });
    }
  }, [fallback, upsell, error, loading, tokens, refining]);

  const pickVerse = (v) => {
    setVerse(v);
    setStructureWord(null);
    if (fallback) return; // fallback covers only the first verse; stay on it
    analyze(v.text);
    track("onboarding-demo-analyze", { verse: v.key });
  };

  const shownSentence = fallback ? FALLBACK.sentence : sentence;
  const shownTokens = fallback ? FALLBACK.tokens : tokens;
  const shownVerse = fallback ? DEMO_VERSES[0] : verse;

  return (
    <div className="w-full">
      <h2 className="text-xl sm:text-2xl font-semibold text-dt-parchment mb-1.5">
        Tap any word
      </h2>
      <p className="text-sm text-dt-parchment-dim mb-4">
        This is a live verse — every word opens its structure and iʿrāb.
      </p>

      {!fallback && (
        <div className="flex flex-wrap gap-2 mb-4">
          {DEMO_VERSES.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => pickVerse(v)}
              className="px-2.5 py-1 rounded-full text-xs border transition-colors"
              style={
                v.key === verse.key
                  ? { color: "var(--gold-bright)", borderColor: "var(--gold)" }
                  : { color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }
              }
            >
              {v.ref}
            </button>
          ))}
        </div>
      )}

      {/* coach-first-word pulses the first tappable word until they tap one */}
      <div className={`surface rounded-xl px-4 py-5 min-h-[7rem] ${tapped ? "" : "coach-first-word"}`}>
        {loading && !fallback ? (
          <p className="text-sm text-dt-parchment-dim text-center py-4">Analyzing…</p>
        ) : shownTokens ? (
          <>
            <ArabicSentence
              text={shownSentence}
              tokens={shownTokens}
              colorToggles={ALL_COLORS}
              refiningIndices={refining}
              onTokenClick={(key, morph) => {
                setStructureWord({ key, morph });
                if (!tapped) {
                  setTapped(true);
                  track("onboarding-demo-word-tap", { verse: shownVerse.key });
                }
              }}
            />
            <p className="text-xs text-dt-parchment-dim mt-4">
              {shownVerse.ref} · verbs, nouns, pronouns and particles each get
              their own color.
            </p>
          </>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="w-full mt-6 px-4 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
      >
        {tapped ? "I want this for every āyah" : "Continue"}
      </button>

      {structureWord && (
        <WordMorphology
          key={`${structureWord.key}:${structureWord.morph?.label || ""}`}
          word={structureWord.morph}
          onClose={() => setStructureWord(null)}
        />
      )}
    </div>
  );
}
